/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable-next-line react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { getDocumentAsync } from 'expo-document-picker';
import Toast from 'react-native-toast-message';

import { formControlContainer } from '@assets/styles/formStyles';
import Colors from '@global/colors';
import FormError from '@components/form/common/FormError';
import FormLabel from '@components/form/common/FormLabel';
import Image from '@components/global/Image';
import ImageContainV2 from '@components/global/ImageContainV2';
import SociollaText from '@components/sociolla/SociollaText';
import {
  uploadS3,
  chooseImagePicker,
  isAllowedAttachmentFormat,
} from '@global/helpers';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import {
  ATTACHMENT_FORMAT_ERROR_TITLE,
  ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
} from '@/constants';
import PropTypes from 'prop-types';
import { isWeb } from '@global/screens';

const propTypes = {
  testID: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  isImage: PropTypes.bool,
  isCSV: PropTypes.bool,
  value: PropTypes.string,
  hasError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  containerStyleExtra: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  showTrash: PropTypes.bool,
  confirmRemove: PropTypes.bool,
  confirmRemoveTitle: PropTypes.string,
  confirmRemoveDescription: PropTypes.string,
  withFileName: PropTypes.bool,
  onChange: PropTypes.func,
};

const defaultProps = {
  testID: 'file-input',
  label: '',
  value: '',
  isRequired: false,
  isImage: false,
  isCSV: false,
  hasError: false,
  containerStyle: {},
  containerStyleExtra: {},
  showTrash: true,
  confirmRemove: false,
  confirmRemoveTitle: 'Remove Document',
  confirmRemoveDescription:
    'This document will be deleted. Do you want to proceed?',
  withFileName: false,
  onChange: () => {},
};

const trash = {
  width: 14,
  height: 18,
};

const imageSize = 200;

const FileInputDefault = (props) => {
  const {
    testID = 'file-input',
    value = '',
    isImage = false,
    isCSV = false,
    containerStyle = {},
    containerStyleExtra = {},
    showTrash = true,
    confirmRemove = false,
    confirmRemoveTitle = 'Remove Document',
    confirmRemoveDescription = 'This document will be deleted. Do you want to proceed?',
    withFileName = false,
    onChange = () => {},
  } = props;

  const dispatch = useDispatch();
  const [filename, setFilename] = useState('');

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setFilename(value);
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const doFindFile = async () => {
    if (isImage) {
      const image = await chooseImagePicker();
      if (image && image.uri) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        try {
          const fileName = image.filename || '';
          const url = await uploadS3({ file: image, isImage: true });
          dispatch(showPopup({ name: 'isLoading', value: false }));
          if (url) {
            try {
              // eslint-disable-next-line react/prop-types
              if (withFileName) {
                onChange({ url, name: fileName });
              } else {
                onChange(url);
              }
            } catch (error) {
              Toast.show({ text1: 'A system error occurred', type: 'error' });
              dispatch(showPopup({ name: 'isLoading', value: false }));
            }
          } else {
            Toast.show({
              text1: 'Failed to upload file. Please try again.',
              type: 'error',
            });
            dispatch(showPopup({ name: 'isLoading', value: false }));
          }
        } catch (error) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
        }
      }
    } else if (isCSV) {
      const pdf = await getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: false,
      });
      if (!pdf.canceled) {
        const failedProcess = () => {
          Toast.show({ text1: 'A system error occurred', type: 'error' });
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(showPopup({ name: 'userCreateOption', value: false }));
        };
        try {
          const fileName = pdf?.assets[0]?.name || '';
          const url = await uploadS3({
            file: pdf?.assets[0] || {},
            isNeedMobileSupport: true,
          });
          if (url) {
            try {
              // eslint-disable-next-line react/prop-types
              if (withFileName) {
                onChange({ url, name: fileName });
              } else {
                onChange(url);
              }
            } catch (error) {
              Toast.show({ text1: 'A system error occurred', type: 'error' });
              dispatch(showPopup({ name: 'isLoading', value: false }));
            }
          } else {
            Toast.show({
              text1: 'Failed to upload file. Please try again.',
              type: 'error',
            });
          }
          dispatch(showPopup({ name: 'isLoading', value: false }));
        } catch (error) {
          console.log(error);
          failedProcess();
        }
      }
    } else {
      const uploadFile = await getDocumentAsync({
        copyToCacheDirectory: false,
      });
      if (!uploadFile.canceled) {
        if (!isAllowedAttachmentFormat(uploadFile.assets[0])) {
          dispatch(
            showAlert({
              show: true,
              title: ATTACHMENT_FORMAT_ERROR_TITLE,
              description: ATTACHMENT_FORMAT_ERROR_DESCRIPTION,
              isConfirm: false,
            }),
          );
          return;
        }
        dispatch(showPopup({ name: 'isLoading', value: true }));
        try {
          const fileName = uploadFile?.assets[0]?.name || '';
          const url = await uploadS3({
            file: uploadFile?.assets[0] || {},
            isNeedMobileSupport: true,
          });
          if (url) {
            try {
              // eslint-disable-next-line react/prop-types
              if (withFileName) {
                onChange({ url, name: fileName });
              } else {
                onChange(url);
              }
            } catch (error) {
              Toast.show({ text1: 'A system error occurred', type: 'error' });
              dispatch(showPopup({ name: 'isLoading', value: false }));
            }
          } else {
            Toast.show({
              text1: 'Failed to upload file. Please try again.',
              type: 'error',
            });
          }
          dispatch(showPopup({ name: 'isLoading', value: false }));
        } catch (err) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
        }
      }
    }
  };

  const onResetConfirmed = () => {
    setFilename('');
    // eslint-disable-next-line react/prop-types
    onChange('');
  };

  const onReset = () => {
    if (confirmRemove) {
      dispatch(
        showAlert({
          show: true,
          title: confirmRemoveTitle,
          description: confirmRemoveDescription,
          onCallback: onResetConfirmed,
        }),
      );
      return;
    }

    onResetConfirmed();
  };

  return (
    <View
      testID={testID}
      style={{
        width: '100%',
        ...formControlContainer,
        ...containerStyle,
      }}>
      <FormLabel {...props} />
      <View style={[styles.container, isCSV ? styles.center : {}]}>
        {filename ? (
          <View style={!isImage ? styles.container : {}}>
            {isImage ? (
              <View style={styles.image}>
                <ImageContainV2 source={value} style={styles.image} />
              </View>
            ) : (
              false
            )}
            <View
              style={[
                isImage
                  ? [styles.container, styles.borderBottomText]
                  : containerStyleExtra,
                isCSV ? styles.isCSV : {},
              ]}>
              <SociollaText
                style={[
                  { maxWidth: isWeb ? '90%' : '100%' },
                  isImage ? { width: imageSize } : {},
                ]}
                numberOfLines={isImage ? 1 : 3}>
                {filename}
              </SociollaText>
              {showTrash ? (
                <TouchableOpacity
                  style={styles.trash}
                  activeOpacity={0.6}
                  onPress={() => onReset()}>
                  <Image
                    style={trash}
                    width={trash.width}
                    height={trash.height}
                    source={require('@assets/img/icons/trash-grey.svg')}
                    svg={true}
                  />
                </TouchableOpacity>
              ) : (
                false
              )}
            </View>
          </View>
        ) : (
          <TouchableOpacity style={styles.upload} onPress={doFindFile}>
            <SociollaText type="medium">Upload</SociollaText>
          </TouchableOpacity>
        )}
        <FormError {...props} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  trash: {
    width: 30,
    height: trash.height,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  upload: {
    width: 150,
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.grey0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: { width: imageSize, marginBottom: 10 },
  borderBottomText: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    paddingBottom: 10,
  },
  isCSV: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '100%',
    alignItems: 'center',
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

FileInputDefault.propTypes = propTypes;
FileInputDefault.defaultProps = defaultProps;

export default React.memo(FileInputDefault, (prev, next) => prev == next);
